import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Template, TemplateFormData } from "../types/template";
import { Category } from "../types/category";
import TemplateForm from "../components/TemplateForm";
import TemplateList from "../components/TemplateList";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { toast } from "sonner";

const TemplatePage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    template: Template | null;
  }>({
    isOpen: false,
    template: null,
  });

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("image_category")
        .select("*")
        .order("category", { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("image_template")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Error fetching templates");
    } finally {
      setLoading(false);
    }
  };

  const getNextImageTemplateId = async () => {
    const { data, error } = await supabase
      .from("image_template")
      .select("template_id")
      .ilike("template_id", "it%")
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) throw error;

    const lastId = data?.[0]?.template_id as string | undefined;
    if (!lastId) return "it1";

    const lastNumber = Number(lastId.replace(/^it/i, ""));
    return Number.isFinite(lastNumber) ? `it${lastNumber + 1}` : "it1";
  };

  const handleCreateTemplate = async (formData: TemplateFormData) => {
    try {
      setLoading(true);
      const imageUrls: string[] = [];

      if (formData.output && formData.output.length > 0) {
        // Upload all files in parallel
        const uploadPromises = Array.from(formData.output).map(async (file) => {
          const fileExt = file.name.split(".").pop();
          const fileName = `${Date.now()}_${Math.random()}.${fileExt}`;
          const filePath = `templates/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("sceneon_image")
            .upload(filePath, file);

          if (uploadError) {
            console.error("Upload error for file:", file.name, uploadError);
            throw uploadError;
          }

          const { data } = supabase.storage
            .from("sceneon_image")
            .getPublicUrl(filePath);

          return data.publicUrl;
        });

        const urls = await Promise.all(uploadPromises);
        imageUrls.push(...urls);
      }

      const nextTemplateId = await getNextImageTemplateId();

      const { data, error } = await supabase
        .from("image_template")
        .insert([
          {
            template_id: nextTemplateId,
            type: formData.type,
            cat_id: formData.cat_id,
            subcategory: formData.subcategory,
            ai_model: formData.ai_model,
            prompt: formData.prompt,
            output: imageUrls, // Send as array
          },
        ])
        .select();

      if (error) throw error;

      setTemplates([data[0], ...templates]);
      setShowForm(false);
      toast.success("Template created successfully!");
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("Error creating template");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async (formData: TemplateFormData) => {
    if (!editingTemplate?.id) return;

    try {
      setLoading(true);
      let imageUrls: string[] = editingTemplate.output || [];

      if (formData.output && formData.output.length > 0) {
        const uploadPromises = Array.from(formData.output).map(async (file) => {
          const fileExt = file.name.split(".").pop();
          const fileName = `${Date.now()}_${Math.random()}.${fileExt}`;
          const filePath = `templates/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("sceneon_image")
            .upload(filePath, file);

          if (uploadError) {
            console.error("Upload error for file:", file.name, uploadError);
            throw uploadError;
          }

          const { data } = supabase.storage
            .from("sceneon_image")
            .getPublicUrl(filePath);

          return data.publicUrl;
        });

        imageUrls = await Promise.all(uploadPromises);
      }

      const { data, error } = await supabase
        .from("image_template")
        .update({
          type: formData.type,
          cat_id: formData.cat_id,
          subcategory: formData.subcategory,
          ai_model: formData.ai_model,
          prompt: formData.prompt,
          output: imageUrls, // Send as array
        })
        .eq("id", editingTemplate.id)
        .select();

      if (error) throw error;

      setTemplates(
        templates.map((t) => (t.id === editingTemplate.id ? data[0] : t))
      );
      setEditingTemplate(null);
      setShowForm(false);
      toast.success("Template updated successfully!");
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Error updating template");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (template: Template) => {
    setDeleteModal({ isOpen: true, template });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.template?.id) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from("image_template")
        .delete()
        .eq("id", deleteModal.template.id);

      if (error) throw error;

      setTemplates(templates.filter((t) => t.id !== deleteModal.template?.id));
      setDeleteModal({ isOpen: false, template: null });
      toast.success("Template deleted successfully!");
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Error deleting template");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, template: null });
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
  };

  // Filter templates based on selected category
  const filteredTemplates =
    selectedCategory === "all"
      ? templates
      : templates.filter(
          (template) => String(template.cat_id) === selectedCategory
        );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Image Templates</h1>
        {!showForm && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-berrys-orange hover:bg-berrys-orange-dark text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Create Template
          </button>
        )}
      </div>

      {/* Category Filter Tabs */}
      {!showForm && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                selectedCategory === "all"
                  ? "bg-berrys-orange text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All ({templates.length})
            </button>
            {categories.map((category) => {
              const categoryTemplatesCount = templates.filter(
                (t) => String(t.cat_id) === String(category.id)
              ).length;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(String(category.id))}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    selectedCategory === String(category.id)
                      ? "bg-berrys-orange text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category.category} ({categoryTemplatesCount})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showForm && (
        <TemplateForm
          onSubmit={
            editingTemplate ? handleUpdateTemplate : handleCreateTemplate
          }
          onCancel={handleCancel}
          loading={loading}
          categories={categories}
          initialData={editingTemplate}
        />
      )}

      {!showForm && filteredTemplates.length === 0 && !loading ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg
            className="mx-auto h-16 w-16 text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No templates found
          </h3>
          <p className="text-gray-500 mb-4">
            {selectedCategory === "all"
              ? "Get started by creating your first template."
              : "No templates found in this category."}
          </p>
          {selectedCategory !== "all" && (
            <button
              onClick={() => setSelectedCategory("all")}
              className="text-berrys-orange hover:text-berrys-orange-dark font-medium"
            >
              View all templates
            </button>
          )}
        </div>
      ) : (
        !showForm && (
          <TemplateList
            templates={filteredTemplates}
            loading={loading}
            categories={categories}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        )
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        itemName={deleteModal.template?.type || deleteModal.template?.prompt}
        loading={loading}
      />
    </div>
  );
};

export default TemplatePage;
